import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormRoot,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, User } from "lucide-react";
import { useCallback } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import { useCreateAdvanceForEmployeeMutation } from "../api/queries";
import {
  AdvanceRequestForm,
  type AdvanceRequestFormValues,
} from "../components/AdvanceRequestForm";

const LIST_SEARCH = {
  offset: 0,
  limit: 20,
  status: undefined as string | undefined,
  employeeId: undefined as string | undefined,
};

const EmployeeSchema = z.object({
  employeeId: z.string().min(1, "ກະລຸນາເລືອກພະນັກງານ"),
});

export function NewAdvanceForEmployeePage() {
  const nav = useNavigate();
  const createMutation = useCreateAdvanceForEmployeeMutation();

  const employeeMethods = RHF.useForm<{ employeeId: string }>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(EmployeeSchema) as any,
    defaultValues: { employeeId: "" },
  });

  const selectedEmployeeId =
    RHF.useWatch({
      control: employeeMethods.control,
      name: "employeeId",
    }) ?? "";

  const handleBack = useCallback(() => {
    nav({ to: "/app/hr/salary-advance", search: LIST_SEARCH });
  }, [nav]);

  const handleSubmit = useCallback(
    (values: AdvanceRequestFormValues) => {
      if (!selectedEmployeeId.trim()) {
        toast.error("ກະລຸນາເລືອກພະນັກງານກ່ອນ");
        return;
      }
      createMutation.mutate(
        {
          employeeId: selectedEmployeeId.trim(),
          requestDate: values.requestDate,
          amount: Number(values.amount),
          reason: values.reason.trim() || null,
        },
        {
          onSuccess: (result) => {
            toast.success("ສ້າງຄຳຂໍສຳເລັດ");
            nav({
              to: "/app/hr/salary-advance/$id",
              params: { id: result.id },
            });
          },
          onError: (err: { message?: string }) => {
            const msg = err?.message ?? "";
            if (msg.includes("ADVANCE_ALREADY_ACTIVE")) {
              toast.error("ພະນັກງານຄົນນີ້ມີຄຳຂໍທີ່ຢູ່ໃນລະຫວ່າງດຳເນີນການຢູ່ແລ້ວ");
              return;
            }
            toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
          },
        },
      );
    },
    [selectedEmployeeId, createMutation, nav],
  );

  return (
    <>
      <Header />
      <Main>
       

        <div className="mb-4 flex items-center justify-between">
        <div className="">
        <h1 className="font-semibold text-2xl">ສ້າງຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ (HR)</h1>
          <p className="text-muted-foreground text-sm">
            ສ້າງຄຳຂໍແທນພະນັກງານ ກໍລະນີພະນັກງານບໍ່ສາມາດດຳເນີນການດ້ວຍຕົວເອງ
          </p>
        </div>

          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            ກັບຄືນລາຍການ
          </Button>
        </div>

        <div className="mx-auto w-full max-w-lg space-y-4">
          {/* Employee selector */}
          <FormRoot<{ employeeId: string }>
            methods={employeeMethods}
            onSubmit={() => {}}
            className="w-full"
          >
            <Card className="w-full">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  ພະນັກງານທີ່ຕ້ອງການສ້າງຄຳຂໍໃຫ້
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormInfiniteCombobox<LookupItem>
                  name="employeeId"
                  label=""
                  requiredMark
                  queryKey={["users-lookup-advance-for-employee"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(
                      `${config.apiUrl}/users/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ຄົ້ນຫາ ແລະ ເລືອກພະນັກງານ..."
                />
              </CardContent>
            </Card>
          </FormRoot>

          {/* Advance form — shown only after employee is selected */}
          {selectedEmployeeId ? (
            <AdvanceRequestForm
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isSubmitting={createMutation.isPending}
            />
          ) : (
            <div className="rounded-xl border border-dashed bg-muted/30 py-10 text-center text-muted-foreground text-sm">
              ກະລຸນາເລືອກພະນັກງານດ້ານເທິງກ່ອນ
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
